import { useCallback, useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { reportMoney, reportMonthToDate, reportMonthWindow, runReport } from '../lib/reports';
import type { DailyProfitDayRow } from '../lib/dailyProfitReport';
import { dayLabel, eachDate, monthLabel as formatMonthLabel } from './useControlCentreReports';

/**
 * Daily profit for the current month, one row per calendar day:
 *
 *   (invoiced sales + jobs scheduled that day not yet invoiced)
 *   − (payroll + live payroll) − expenses − operating costs
 *
 * The scheduled part is what makes it a day-to-day planning number: it
 * answers "is what I have on the books today enough to cover today's
 * costs", before the invoices go out. Both halves come from the report
 * system (profit and scheduled_sales datasets).
 */
export function useDailyProfitMonthBreakdown() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timeZone = selectedBusiness?.timezone || 'America/Vancouver';

  const [days, setDays] = useState<DailyProfitDayRow[]>([]);
  const [monthTotal, setMonthTotal] = useState(0);
  const [monthLabel, setMonthLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!businessId) {
      setDays([]);
      setMonthTotal(0);
      setMonthLabel('');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const month = reportMonthWindow(timeZone, 0);
      const today = reportMonthToDate(timeZone, 0).until;
      setMonthLabel(formatMonthLabel(month.since));

      const window = `SINCE ${month.since} UNTIL ${month.until}`;
      const [profit, scheduled] = await Promise.all([
        runReport(businessId, `FROM profit SHOW sales, payroll, live_payroll, expenses, operating_costs GROUP BY day ${window}`),
        runReport(businessId, `FROM scheduled_sales SHOW total_scheduled_sales GROUP BY day ${window}`),
      ]);
      const profitByDate = new Map(profit.rows.map((row) => [String(row.day), row]));
      const scheduledByDate = new Map(scheduled.rows.map((row) => [String(row.day), row]));

      const rows: DailyProfitDayRow[] = eachDate(month.since, month.until).map((date) => {
        const row = profitByDate.get(date);
        const pick = (metric: string) => (row ? reportMoney({ ...profit, rows: [row] }, metric) : 0);
        const scheduledRow = scheduledByDate.get(date);
        const scheduledAmount = scheduledRow ? reportMoney({ ...scheduled, rows: [scheduledRow] }, 'total_scheduled_sales') : 0;
        const sales = pick('sales');
        const labor = pick('payroll') + pick('live_payroll');
        const directExpenses = pick('expenses');
        const overheadExpenses = pick('operating_costs');
        const revenue = sales + scheduledAmount;
        return {
          date,
          label: dayLabel(date),
          revenue,
          scheduled: scheduledAmount,
          labor,
          overheadExpenses,
          directExpenses,
          profit: revenue - labor - directExpenses - overheadExpenses,
          isToday: date === today,
        };
      });

      setDays(rows);
      // Month total stops at today: future days are plans, not results.
      setMonthTotal(rows.filter((row) => row.date <= today).reduce((sum, row) => sum + row.profit, 0));
    } catch (err) {
      console.error('Error loading daily profit breakdown:', err);
      setDays([]);
      setMonthTotal(0);
      setError(err instanceof Error ? err.message : 'Could not load daily profit report.');
    } finally {
      setLoading(false);
    }
  }, [businessId, timeZone]);

  useEffect(() => {
    setLoading(true);
    void load();
    const interval = setInterval(() => void load(), 60_000);
    return () => clearInterval(interval);
  }, [load]);

  return { days, monthTotal, monthLabel, loading, error, load };
}
